import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_CONTAINER_ID, DEFAULT_IMAGE_LAYER, DEFAULT_LAYERS, DEFAULT_SIMPLE_LAYER, DEFAULT_USER_CONFIG, } from "@erikwatson/snowfall";
import { merge2 } from "@erikwatson/snowfall";
import { PRESET_SNOW } from "../../presets/snow";
const initialState = PRESET_SNOW.userConfig;
const configSlice = createSlice({
    name: "userConfig",
    initialState,
    reducers: {
        resetUserConfig: (state) => {
            return DEFAULT_USER_CONFIG;
        },
        setUserConfig: (state, action) => {
            return merge2(action.payload);
        },
        // attach to
        resetAttachTo: (state) => {
            state.attachTo = DEFAULT_CONTAINER_ID;
        },
        setAttachTo: (state, action) => {
            state.attachTo = action.payload;
        },
        // layers
        resetLayers: (state) => {
            state.layers = DEFAULT_LAYERS;
        },
        resetLayer: (state, action) => {
            state.layers[action.payload] =
                state.layers[action.payload].mode === "simple"
                    ? DEFAULT_SIMPLE_LAYER
                    : DEFAULT_IMAGE_LAYER;
        },
        setLayers: (state, action) => {
            state.layers = action.payload;
        },
        setLayer: (state, action) => {
            if (state.layers) {
                state.layers[action.payload.index] = action.payload.layer;
            }
        },
        addLayer: (state, action) => {
            if (state.layers) {
                state.layers.push(action.payload);
            }
        },
        removeLayer: (state, action) => {
            if (state.layers) {
                state.layers.splice(action.payload, 1);
            }
        },
        updateLayer: (state, action) => {
            if (state.layers) {
                state.layers[action.payload.index] = action.payload.layer;
            }
        },
        duplicateLayer: (state, action) => {
            if (state.layers) {
                state.layers.push(state.layers[action.payload]);
            }
        },
        setColour: (state, action) => {
            const layer = state.layers?.[action.payload.index];
            if (layer && layer.mode === "simple") {
                layer.colour = action.payload.colour;
            }
        },
        setTrail: (state, action) => {
            const layer = state.layers?.[action.payload.index];
            if (layer && layer.mode === "simple") {
                layer.trail = action.payload.trail;
            }
        },
        setDensity: (state, action) => {
            if (action.payload.index !== undefined) {
                if (state.layers) {
                    state.layers[action.payload.index].density = action.payload.density;
                }
            }
            else {
                state.layers?.forEach((layer) => {
                    layer.density = action.payload.density;
                });
            }
        },
        setMass: (state, action) => {
            if (state.layers) {
                state.layers[action.payload.index].mass = action.payload.mass;
            }
        },
        setMassMax: (state, action) => {
            if (action.payload.index !== undefined) {
                if (action.payload.max < state.layers[action.payload.index].mass.min) {
                    state.layers[action.payload.index].mass.min = action.payload.max;
                }
                state.layers[action.payload.index].mass.max = action.payload.max;
            }
            else {
                state.layers.forEach((layer) => {
                    if (action.payload.max < layer.mass.min) {
                        layer.mass.min = action.payload.max;
                    }
                    layer.mass.max = action.payload.max;
                });
            }
        },
        setMassMin: (state, action) => {
            // we need to set the max mass to the min mass if it's less than the min mass
            if (action.payload.index !== undefined) {
                if (action.payload.min > state.layers[action.payload.index].mass.max) {
                    state.layers[action.payload.index].mass.max = action.payload.min;
                }
                state.layers[action.payload.index].mass.min = action.payload.min;
            }
            else {
                state.layers.forEach((layer) => {
                    if (action.payload.min > layer.mass.max) {
                        layer.mass.max = action.payload.min;
                    }
                    layer.mass.min = action.payload.min;
                });
            }
        },
        setSize: (state, action) => {
            if (state.layers) {
                state.layers[action.payload.index].mass = action.payload.size;
            }
        },
        setSizeMax: (state, action) => {
            if (action.payload.index !== undefined) {
                if (action.payload.max < state.layers[action.payload.index].size.min) {
                    state.layers[action.payload.index].size.min = action.payload.max;
                }
                state.layers[action.payload.index].size.max = action.payload.max;
            }
            else {
                state.layers.forEach((layer) => {
                    if (action.payload.max < layer.size.min) {
                        layer.size.min = action.payload.max;
                    }
                    layer.size.max = action.payload.max;
                });
            }
        },
        setSizeMin: (state, action) => {
            // we need to set the max size to the min size if it's less than the min size
            if (action.payload.index !== undefined) {
                if (action.payload.min > state.layers[action.payload.index].size.max) {
                    state.layers[action.payload.index].size.max = action.payload.min;
                }
                state.layers[action.payload.index].size.min = action.payload.min;
            }
            else {
                state.layers.forEach((layer) => {
                    if (action.payload.min > layer.size.max) {
                        layer.size.max = action.payload.min;
                    }
                    layer.size.min = action.payload.min;
                });
            }
        },
        setOpacity: (state, action) => {
            state.layers[action.payload.index].opacity = action.payload.opacity;
        },
        setOpacityMax: (state, action) => {
            const apply = (layer) => {
                layer.opacity.max = action.payload.max;
                if (layer.opacity.min > layer.opacity.max) {
                    layer.opacity.min = layer.opacity.max;
                }
            };
            if (action.payload.index !== undefined) {
                apply(state.layers[action.payload.index]);
            }
            else {
                state.layers.forEach(apply);
            }
        },
        setOpacityMin: (state, action) => {
            const apply = (layer) => {
                layer.opacity.min = action.payload.min;
                if (layer.opacity.max < layer.opacity.min) {
                    layer.opacity.max = layer.opacity.min;
                }
            };
            if (action.payload.index !== undefined) {
                apply(state.layers[action.payload.index]);
            }
            else {
                state.layers.forEach(apply);
            }
        },
        // setOpacity: (
        //   state,
        //   action: PayloadAction<{ index: number; opacity: Types.SizeBounds }>,
        // ) => {
        //   if (state.layers) {
        //     state.layers[action.payload.index].opacity = action.payload.opacity;
        //   }
        // },
        // setOpacityMax: (
        //   state,
        //   action: PayloadAction<{ index?: number; max: number }>,
        // ) => {
        //   if (action.payload.index !== undefined) {
        //     const layer = state.layers[action.payload.index];
        //     if (action.payload.max < layer.opacity.min) {
        //       layer.opacity.min = action.payload.max;
        //     }
        //     layer.opacity.max = action.payload.max;
        //   } else {
        //     state.layers.forEach((layer) => {
        //       if (action.payload.max < layer.opacity.min) {
        //         layer.opacity.min = action.payload.max;
        //       }
        //       layer.opacity.max = action.payload.max;
        //     });
        //   }
        // },
        // setOpacityMin: (
        //   state,
        //   action: PayloadAction<{ index?: number; min: number }>,
        // ) => {
        //   if (action.payload.index !== undefined) {
        //     const layer = state.layers[action.payload.index];
        //     if (action.payload.min > layer.opacity.max) {
        //       layer.opacity.max = action.payload.min;
        //     }
        //     layer.opacity.min = action.payload.min;
        //   } else {
        //     state.layers.forEach((layer) => {
        //       if (action.payload.min > layer.opacity.max) {
        //         layer.opacity.max = action.payload.min;
        //       }
        //       layer.opacity.min = action.payload.min;
        //     });
        //   }
        // },
        setGravity: (state, action) => {
            state.layers[action.payload.index].gravity = action.payload.gravity;
        },
        setGravityAngle: (state, action) => {
            state.layers[action.payload.index].gravity.angle = action.payload.angle;
        },
        setGravityStrength: (state, action) => {
            state.layers[action.payload.index].gravity.strength =
                action.payload.strength;
        },
        setWind: (state, action) => {
            state.layers[action.payload.index].wind = action.payload.wind;
        },
        setWindAngle: (state, action) => {
            state.layers[action.payload.index].wind.angle = action.payload.angle;
        },
        setWindStrength: (state, action) => {
            if (action.payload.index !== undefined) {
                state.layers[action.payload.index].wind.strength =
                    action.payload.strength;
            }
            else {
                state.layers.forEach((layer) => {
                    layer.wind.strength = action.payload.strength;
                });
            }
        },
        setWindGusts: (state, action) => {
            state.layers[action.payload.index].wind.gusts = action.payload.gusts;
        },
        setWindGustsActive: (state, action) => {
            // state.layers[action.payload.index].wind.gusts.active = action.payload.active
            if (action.payload.index !== undefined) {
                state.layers[action.payload.index].wind.gusts.active =
                    action.payload.active;
            }
            else {
                state.layers.forEach((layer) => {
                    layer.wind.gusts.active = action.payload.active;
                });
            }
        },
        setWindGustsIn: (state, action) => {
            state.layers[action.payload.index].wind.gusts.in = action.payload.in;
        },
        setWindGustsInDuration: (state, action) => {
            state.layers[action.payload.index].wind.gusts.in.duration =
                action.payload.duration;
        },
        setWindGustsInDurationMax: (state, action) => {
            state.layers[action.payload.index].wind.gusts.in.duration.max =
                action.payload.max;
        },
        setWindGustsInDurationMin: (state, action) => {
            state.layers[action.payload.index].wind.gusts.in.duration.min =
                action.payload.min;
        },
        setWindGustsInAdditionalStrength: (state, action) => {
            state.layers[action.payload.index].wind.gusts.in.additionalStrength =
                action.payload.additionalStrength;
        },
        setWindGustsInAdditionalStrengthMax: (state, action) => {
            state.layers[action.payload.index].wind.gusts.in.additionalStrength.max =
                action.payload.max;
        },
        setWindGustsInAdditionalStrengthMin: (state, action) => {
            state.layers[action.payload.index].wind.gusts.in.additionalStrength.min =
                action.payload.min;
        },
        setWindGustsInDelay: (state, action) => {
            state.layers[action.payload.index].wind.gusts.in.delay =
                action.payload.delay;
        },
        setWindGustsInDelayMax: (state, action) => {
            state.layers[action.payload.index].wind.gusts.in.delay.max =
                action.payload.max;
        },
        setWindGustsInDelayMin: (state, action) => {
            state.layers[action.payload.index].wind.gusts.in.delay.min =
                action.payload.min;
        },
        setWindGustsOut: (state, action) => {
            state.layers[action.payload.index].wind.gusts.out = action.payload.out;
        },
        setWindGustsOutDuration: (state, action) => {
            state.layers[action.payload.index].wind.gusts.out.duration =
                action.payload.duration;
        },
        setWindGustsOutDurationMax: (state, action) => {
            state.layers[action.payload.index].wind.gusts.out.duration.max =
                action.payload.max;
        },
        setWindGustsOutDurationMin: (state, action) => {
            state.layers[action.payload.index].wind.gusts.out.duration.min =
                action.payload.min;
        },
        setWindGustsOutDelay: (state, action) => {
            state.layers[action.payload.index].wind.gusts.out.delay =
                action.payload.delay;
        },
        setWindGustsOutDelayMax: (state, action) => {
            state.layers[action.payload.index].wind.gusts.out.delay.max =
                action.payload.max;
        },
        setWindGustsOutDelayMin: (state, action) => {
            state.layers[action.payload.index].wind.gusts.out.delay.min =
                action.payload.min;
        },
        setSway: (state, action) => {
            state.layers[action.payload.index].sway = action.payload.sway;
        },
        setSwayAmplitude: (state, action) => {
            state.layers[action.payload.index].sway.amplitude =
                action.payload.amplitude;
        },
        setSwayFrequency: (state, action) => {
            state.layers[action.payload.index].sway.frequency =
                action.payload.frequency;
        },
        setWindGustsChangeChance: (state, action) => {
            if (action.payload.index !== undefined) {
                state.layers[action.payload.index].wind.gusts.changeChance =
                    action.payload.chance;
            }
            else {
                state.layers.forEach((layer) => {
                    layer.wind.gusts.changeChance = action.payload.chance;
                });
            }
        },
        moveLayerUp: (state, action) => {
            const { index } = action.payload;
            if (state.layers && index > 0 && index < state.layers.length) {
                // Swap the layer at currentIndex with the one at currentIndex - 1
                const temp = state.layers[index];
                state.layers[index] = state.layers[index - 1];
                state.layers[index - 1] = temp;
            }
        },
        moveLayerDown: (state, action) => {
            const { index } = action.payload;
            if (state.layers && index >= 0 && index < state.layers.length - 1) {
                // Swap the layer at currentIndex with the one at currentIndex + 1
                const temp = state.layers[index];
                state.layers[index] = state.layers[index + 1];
                state.layers[index + 1] = temp;
            }
        },
        setLayerRotation: (state, action) => {
            const { rotate, index } = action.payload;
            state.layers[index].rotate = rotate;
        },
        setLayerImage: (state, action) => {
            const { image, index } = action.payload;
            state.layers[index].image = image;
        },
        setLayerScroll: (state, action) => {
            const { scroll, index } = action.payload;
            (state.layers[index]).scroll = scroll;
        },
    },
});
export const { resetUserConfig, setUserConfig, resetAttachTo, setAttachTo, setColour, setDensity, setWindGustsActive, setMassMax, setMassMin, setWindStrength, setWindGustsChangeChance, addLayer, resetLayers, removeLayer, duplicateLayer, resetLayer, setGravityAngle, setGravityStrength, setWindGustsInAdditionalStrength, setWindGustsInAdditionalStrengthMax, setWindGustsInAdditionalStrengthMin, setWindGustsInDelay, setWindGustsInDelayMax, setWindGustsInDelayMin, setWindGustsInDuration, setWindGustsInDurationMax, setWindGustsInDurationMin, setWindGustsOutDelay, setWindGustsOutDelayMax, setWindGustsOutDelayMin, setWindGustsOutDuration, setWindGustsOutDurationMax, setWindGustsOutDurationMin, setMass, setSway, setSwayAmplitude, setSwayFrequency, setWindAngle, moveLayerUp, moveLayerDown, setLayerRotation, setLayerScroll, setLayerImage, setSizeMin, setSizeMax, setSize, setOpacity, setOpacityMin, setOpacityMax, setTrail } = configSlice.actions;
export default configSlice.reducer;
